/**
 * Gen-UI block migration chain — same pattern as Zustand persist migrations.
 *
 * // v1 → v2: KVList renamed to KeyValueList (renameBlockName)
 * spec => renameBlockName(spec, { from: 'KVList', to: 'KeyValueList' }),
 *
 * // v2 → v3: NetworkGraph "height" renamed to "maxHeight" (renameBlockProp)
 * spec => renameBlockProp(spec, { name: 'NetworkGraph', from: 'height', to: 'maxHeight' }),
 *
 * // v3 → v4: DataTable columns[].fieldName renamed to columns[].accessor (inline, nested)
 * spec => transformBlockByName(spec, { name: 'DataTable', transform: el => {
 *   if (!Array.isArray(el.props.columns)) return el;
 *   return {
 *     ...el,
 *     props: {
 *       ...el.props,
 *       columns: (el.props.columns as Array<Record<string, unknown>>).map(col => {
 *         const { fieldName, ...rest } = col;
 *         return { accessor: col.accessor ?? fieldName, ...rest };
 *       }),
 *     },
 *   };
 * }}),
 *
 * Then bump CURRENT_SCHEMA_VERSION by appending to MIGRATION_CHAIN.
 */

import type { Spec } from '@json-render/core';

export type SpecElement = { type: string; props: Record<string, unknown>; children: string[] };
type SpecElements = Record<string, SpecElement>;
export type MigrationFn = (spec: Spec) => Spec;

export const transformBlockByName = (
  spec: Spec,
  { name, transform }: { name: string; transform: (el: SpecElement) => SpecElement },
): Spec => {
  const elements = { ...(spec.elements as SpecElements) };
  for (const id of Object.keys(elements)) {
    if (elements[id].type === name) {
      elements[id] = transform(elements[id]);
    }
  }
  return { ...spec, elements } as Spec;
};

/** Rename a component type across the entire spec. Use when a component is renamed in registry.tsx. */
export const renameBlockName = (spec: Spec, { from, to }: { from: string; to: string }): Spec => {
  return transformBlockByName(spec, { name: from, transform: el => ({ ...el, type: to }) });
};

/** Rename a top-level prop on all elements of a given component type. */
export const renameBlockProp = (spec: Spec, { name, from, to }: { name: string; from: string; to: string }): Spec => {
  return transformBlockByName(spec, {
    name,
    transform: el => {
      if (!el.props || !(from in el.props)) return el;
      const { [from]: value, ...rest } = el.props;
      return { ...el, props: { ...rest, [to]: value } };
    },
  });
};

// MIGRATION_CHAIN[i] upgrades blocks from version (i+1) to (i+2).
const MIGRATION_CHAIN: MigrationFn[] = [
  // v1 → v2: CalloutBox removed — convert to a Markdown element preserving title/body.
  spec =>
    transformBlockByName(spec, {
      name: 'CalloutBox',
      transform: el => {
        const title = typeof el.props.title === 'string' ? el.props.title : '';
        const body = typeof el.props.body === 'string' ? el.props.body : '';
        const content = title ? `**${title}**\n\n${body}` : body;
        return { ...el, type: 'Markdown', props: { content } };
      },
    }),
];

export const CURRENT_SCHEMA_VERSION = MIGRATION_CHAIN.length + 1;

export const applyMigrations = (spec: Spec, version: number, chain: MigrationFn[]): Spec => {
  if (chain.length === 0 || version >= chain.length + 1) return spec;

  const fromIndex = Math.max(version - 1, 0);
  let current = spec;
  for (let i = fromIndex; i < chain.length; i++) {
    try {
      current = chain[i](current);
    } catch (err) {
      console.error(`[GenUi:migrations] step v${i + 1}→v${i + 2} failed — rendering block as-is:`, err);
      return current;
    }
  }
  return current;
};

export const migrateSpec = (spec: Spec, version: number): Spec => {
  return applyMigrations(spec, version, MIGRATION_CHAIN);
};

export const getSchemaVersion = (metadata?: Record<string, unknown>): number | undefined => {
  const raw = metadata?.schema_version;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};
